import { useState, useRef, useEffect, memo } from "react";

const PLACEHOLDER_BLUR = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='40' height='40' fill='%23111318'/%3E%3C/svg%3E";

const OptimizedImage = memo(({ 
  src, alt, width, height, className = "", 
  priority = false, sizes, aspectRatio,
  onLoad: onLoadProp
}) => {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(priority);
  const imgRef = useRef(null);

  useEffect(() => {
    if (priority || !imgRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { rootMargin: "200px" }
    );
    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [priority]);

  const handleLoad = () => {
    setLoaded(true);
    onLoadProp?.();
  };

  const style = {};
  if (aspectRatio) style.aspectRatio = aspectRatio;
  if (width) style.width = typeof width === "number" ? `${width}px` : width;
  if (height) style.height = typeof height === "number" ? `${height}px` : height;

  return (
    <div
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={style}
    >
      {/* Blur placeholder */}
      {!loaded && (
        <div 
          className="absolute inset-0 bg-[#111318] animate-pulse"
          style={{ backgroundImage: `url("${PLACEHOLDER_BLUR}")`, backgroundSize: "cover" }}
        />
      )}
      {inView && (
        <img
          src={src}
          alt={alt || ""}
          width={width}
          height={height}
          sizes={sizes}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          fetchPriority={priority ? "high" : "auto"}
          onLoad={handleLoad}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
          style={{ contentVisibility: "auto" }}
        />
      )}
    </div>
  );
});

OptimizedImage.displayName = "OptimizedImage";
export { OptimizedImage };
